<template>
  <div class="payment-create main-content">
    <form action="" @submit.prevent="submitPaymentData">
      <div class="page-header">
        <h3 class="page-title">{{ $t("payments.new_payment") }}</h3>
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/funder/dashboard">{{
              $t("general.home")
            }}</router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/funder/payments">{{
              $tc("payments.payment", 2)
            }}</router-link>
          </li>
          <li class="breadcrumb-item">{{ $t("payments.new_payment") }}</li>
        </ol>
        <div class="page-actions header-button-container">
          <base-button
            v-if="payment_button_show"
            :loading="isLoading"
            :disabled="isLoading"
            icon="save"
            color="theme"
            type="submit"
          >
            {{ $t("payments.register_payment") }}
          </base-button>
          <span class="text-danger" v-else>
            Credit Limit Reached Or Set Credit Limit</span
          >
        </div>
      </div>
      <div class="payment-card card">
        <div class="card-body">
          <div class="row">
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t("payments.date") }}</label
                ><span class="text-danger"> *</span>
                <base-date-picker
                  v-model="formData.payment_date"
                  :calendar-button="true"
                  calendar-button-icon="calendar"
                  :invalid="$v.formData.payment_date.$error"
                  @change="$v.formData.payment_date.$touch()"
                />
                <div v-if="$v.formData.payment_date.$error">
                  <span
                    v-if="!$v.formData.payment_date.required"
                    class="text-danger"
                    >{{ $t("validation.required") }}</span
                  >
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{
                  $tc("payments.po_type", 1)
                }}</label>
                <base-select
                  v-model="payment_type"
                  :options="paymentTypes"
                  :searchable="true"
                  :show-labels="false"
                  :placeholder="$t('payments.select_payment_type')"
                  label="name"
                  :invalid="$v.payment_type.$error"
                >
                </base-select>
                <div v-if="$v.payment_type.$error">
                  <span v-if="!$v.payment_type.required" class="text-danger">{{
                    $t("validation.required")
                  }}</span>
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <label class="form-label">{{ $t("payments.customer") }}</label
              ><span class="text-danger"> *</span>
              <base-select
                ref="baseSelect"
                v-model="customer"
                :options="customerList"
                :searchable="true"
                :show-labels="false"
                :allow-empty="false"
                :placeholder="$t('customers.select_a_customer')"
                label="name"
                track-by="id"
                :invalid="$v.customer.$error"
              />
              <div v-if="$v.customer.$error">
                <span v-if="!$v.customer.required" class="text-danger">{{
                  $tc("validation.required")
                }}</span>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t("pos.ref_num") }}</label
                ><span class="text-danger"> *</span>
                <base-select
                  v-model="invoice"
                  :options="invoiceList"
                  :invalid="$v.invoice.$error"
                  :searchable="true"
                  :show-labels="false"
                  :allow-empty="false"
                  :placeholder="$t('invoices.select_invoice')"
                  :custom-label="invoiceWithAmount"
                  track-by="id"
                />
                <div v-if="$v.invoice.$error">
                  <span v-if="!$v.invoice.required" class="text-danger">{{
                    $tc("validation.required")
                  }}</span>
                </div>
              </div>
            </div>
            <!-- <div class="col-sm-6">
                              <div class="form-group">
                                  <label class="form-label">{{ $t('payments.payment_number') }}</label><span class="text-danger"> *</span>
                                  <base-prefix-input
                                  v-model.trim="paymentNumAttribute"
                                  :prefix="paymentPrefix"
                                  :readonly="true"
                                  :invalid="$v.paymentNumAttribute.$error"
                                  @input="$v.paymentNumAttribute.$touch()"
                                  />
                                  <div v-if="$v.paymentNumAttribute.$error">
                                  <span v-if="!$v.paymentNumAttribute.required" class="text-danger">{{ $tc('validation.required') }}</span>
                                  <span v-if="!$v.paymentNumAttribute.numeric" class="text-danger">{{ $tc('validation.numbers_only') }}</span>
                                  </div>
                              </div>
                          </div> -->
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{
                  $t("payments.payment_mode")
                }}</label>
                <base-select
                  v-model="formData.payment_method"
                  :options="paymentModes"
                  :searchable="true"
                  :show-labels="false"
                  :placeholder="$t('payments.select_payment_mode')"
                  label="name"
                >
                  <div slot="afterList">
                    <button
                      type="button"
                      class="list-add-button"
                      @click="addPaymentMode"
                    >
                      <font-awesome-icon class="icon" icon="cart-plus" />
                      <label>{{
                        $t("settings.customization.payments.add_payment_mode")
                      }}</label>
                    </button>
                  </div>
                </base-select>
              </div>
            </div>
            <div
              class="col-sm-6"
              v-show="formData.payment_type == 'Prepayment'"
            >
              <div class="form-group">
                <label class="form-label">{{ $t("payments.amount") }}</label
                ><span class="text-danger"> *</span>
                <div class="base-input">
                  <money
                    :class="{ invalid: $v.formData.amount.$error }"
                    v-model="amount"
                    v-bind="paymentCurrency"
                    class="input-field"
                    :readonly="amountReady"
                  />
                </div>

                <div v-if="$v.formData.amount.$error">
                  <span
                    v-if="!$v.formData.amount.required"
                    class="text-danger"
                    >{{ $t("validation.required") }}</span
                  >
                  <span
                    v-if="
                      !$v.formData.amount.between &&
                        $v.formData.amount.numeric &&
                        amount <= 0
                    "
                    class="text-danger"
                    >{{ $t("validation.payment_greater_than_zero") }}</span
                  >
                  <span
                    v-if="!$v.formData.amount.between && amount > 0"
                    class="text-danger"
                    >{{
                      $t("validation.payment_greater_than_offered_amount")
                    }}</span
                  >
                  <span
                    v-if="!$v.formData.amount.between"
                    class="text-danger"
                  ></span>
                </div>
              </div>
            </div>
            <div class="col-sm-6" v-if="formData.payment_type == 'Settlement'">
              <div class="form-group">
                <label class="form-label">{{
                  $t("general.loanable_amount")
                }}</label
                ><span class="text-danger"> *</span>
                <div class="base-input">
                  <money
                    :disabled="'true'"
                    v-model="LoanableAmount"
                    v-bind="paymentCurrency"
                    class="input-field"
                  />
                </div>
              </div>
            </div>

            <div class="col-sm-6" v-if="formData.payment_type == 'Settlement'">
              <div class="form-group">
                <label class="form-label">{{
                  $t("settlements.supplier_settlement")
                }}</label
                ><span class="text-danger"> *</span>
                <div class="base-input">
                  <money
                    :disabled="'true'"
                    v-model="supplier_settlement"
                    v-bind="paymentCurrency"
                    class="input-field"
                    readonly
                  />
                </div>
              </div>
            </div>
            <div class="col-sm-12 ">
              <div class="form-group">
                <label class="form-label">{{ $t("payments.note") }}</label>
                <base-text-area
                  v-model="formData.notes"
                  @input="$v.formData.notes.$touch()"
                />
                <div v-if="$v.formData.notes.$error">
                  <span
                    v-if="!$v.formData.notes.maxLength"
                    class="text-danger"
                    >{{ $t("validation.notes_maxlength") }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="payment-card card"
        v-if="formData.payment_type == 'Settlement'"
      >
        <div class="card-body">
          <div class="row">
            <!-- <div class="col-sm-6">
                              <div class="form-group">
                                  <label class="form-label">{{ $t('payments.payment_number') }}</label>
                                  <base-prefix-input
                                  v-model.trim="secondPaymentNumAttribute"
                                  :prefix="paymentPrefix"
                                  :readonly="true"
                                  :invalid="$v.secondPaymentNumAttribute.$error"
                                  @input="$v.secondPaymentNumAttribute.$touch()"
                                  />
                                  <div v-if="$v.secondPaymentNumAttribute.$error">
                                  <span v-if="!$v.secondPaymentNumAttribute.required" class="text-danger">{{ $tc('validation.required') }}</span>
                                  <span v-if="!$v.secondPaymentNumAttribute.numeric" class="text-danger">{{ $tc('validation.numbers_only') }}</span>
                                  </div>
                              </div>
                          </div> -->

            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{
                  $t("payments.payment_mode")
                }}</label>
                <base-select
                  v-model="formData.second_payment_method"
                  :options="paymentModes"
                  :searchable="true"
                  :show-labels="false"
                  :placeholder="$t('payments.select_payment_mode')"
                  label="name"
                >
                  <div slot="afterList">
                    <button
                      type="button"
                      class="list-add-button"
                      @click="addPaymentMode"
                    >
                      <font-awesome-icon class="icon" icon="cart-plus" />
                      <label>{{
                        $t("settings.customization.payments.add_payment_mode")
                      }}</label>
                    </button>
                  </div>
                </base-select>
              </div>
            </div>

            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{
                  $t("general.transaction_fees")
                }}</label
                ><span class="text-danger"> *</span>
                <div class="base-input">
                  <money
                    :disabled="'true'"
                    v-model="super_admin_settlement"
                    v-bind="paymentCurrency"
                    class="input-field"
                    readonly
                  />
                </div>
              </div>
            </div>
            <div class="col-sm-12 ">
              <div class="form-group">
                <label class="form-label">{{ $t("payments.note") }}</label>
                <base-text-area
                  v-model="superadmin_settlement_notes"
                  @input="$v.superadmin_settlement_notes.$touch()"
                />
                <div v-if="$v.superadmin_settlement_notes.$error">
                  <span
                    v-if="!$v.superadmin_settlement_notes.maxLength"
                    class="text-danger"
                    >{{ $t("validation.notes_maxlength") }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import MultiSelect from "vue-multiselect";
import { validationMixin } from "vuelidate";
import moment from "moment";
const {
  required,
  between,
  maxLength,
  numeric
} = require("vuelidate/lib/validators");
export default {
  mixins: [validationMixin],
  data() {
    return {
      isLoading: false,
      amountReady: false,
      formData: {
        customer_id: null,
        customer_company_id: null,

        payment_number: null,
        second_payment_number: null,

        payment_date: null,
        amount: 0,
        payment_method: null,
        second_payment_method: null,
        third_payment_method: null,
        purchase_order_id: null,
        notes: null,

        payment_type: null,

        super_admin_settlement: 0,
        supplier_settlement: 0,
        loanableAmount: 0
      },
      payment_type: null,
      paymentNumAttribute: 0,
      secondPaymentNumAttribute: 0,

      paymentPrefix: "",
      paymentTypes: [{ name: "Prepayment" }, { name: "Settlement" }],
      customerList: [],
      customer: null,
      invoiceList: [],
      invoice: null,
      maxPayableAmount: Number.MAX_SAFE_INTEGER,
      customerCurrency: null,

      maxSuperadminAmount: Number.MAX_SAFE_INTEGER,
      maxSupplierAmount: Number.MAX_SAFE_INTEGER,

      superadmin_settlement_notes: null,
      cashflowValue: 0,
      margin_rate: 0,
      transaction_fees: 0,
      payment_button_show: true,
      tenor: 0,
      loanOfferedDate: null
    };
  },
  validations() {
    return {
      customer: {
        required
      },
      payment_type: {
        required
      },

      superadmin_settlement_notes: {
        maxLength: maxLength(255)
      },

      invoice: {
        required
      },
      formData: {
        payment_date: {
          required
        },
        amount: {
          required,
          between: between(1, this.maxPayableAmount + 1)
        },
        notes: {
          maxLength: maxLength(255)
        }
      }
      // paymentNumAttribute: {
      //     required,
      //     numeric
      // },
      // secondPaymentNumAttribute:{
      //     required,
      //     numeric
      // },
    };
  },
  created() {
    if (this.isInvoice) {
      this.loadPaymentData();
    }
  },
  computed: {
    ...mapGetters("currency", ["defaultCurrencyForInput"]),
    ...mapGetters("payment", ["paymentModes"]),
    amount: {
      get: function() {
        return this.$utils.formatGraphMoney(
          this.formData.amount,
          this.customerCurrency
        );
      },
      set: function(newValue) {
        this.formData.amount = newValue;
      }
    },
    paymentCurrency() {
      if (this.customer && this.customerCurrency) {
        console.log(
          "this.customer && this.customerCurrency ",
          this.customer,
          this.customerCurrency
        );
        return {
          decimal: this.customerCurrency.decimal_separator,
          thousands: this.customerCurrency.thousand_separator,
          prefix: this.customerCurrency.symbol + " ",
          precision: this.customerCurrency.precision,
          masked: false
        };
      } else {
        return this.defaultCurrencyForInput;
      }
    },

    isInvoice() {
      if (
        this.$route.name === "payments.po-prepayment.create" ||
        this.$route.name === "payments.po-settlement.create"
      ) {
        return true;
      }
      return false;
    },
    LoanableAmount: {
      get: function() {
        return (this.margin_rate / 100) * (this.cashflowValue / 100);
      },
      set: function(newValue) {
        this.formData.loanableAmount = newValue * 100;
      }
    },
    supplier_settlement: {
      get: function() {
        if (this.LoanableAmount && this.cashflowValue) {
          return this.cashflowValue / 100 - this.LoanableAmount;
        }
      },
      set: function(newValue) {
        if (this.LoanableAmount && this.cashflowValue) {
          this.formData.supplier_settlement = newValue * 100;
        }
      }
    },
    super_admin_settlement: {
      get: function() {
        if (this.LoanableAmount) {
          return (
            (this.transaction_fees / 100) *
            this.LoanableAmount *
            (this.tenor / 365)
          );
        }
      },
      set: function(newValue) {
        if (this.LoanableAmount) {
          this.formData.super_admin_settlement = newValue * 100;
        }
      }
    }
  },
  watch: {
    payment_type(newValue) {
      this.payment_button_show = true;
      this.formData.amount = 0;
      this.invoiceList = [];
      this.customerList = [];
      this.customer = null;
      this.invoice = null;
      if (newValue.name == "Prepayment") {
        this.formData.payment_type = newValue.name;
      } else if (newValue.name == "Settlement") {
        this.formData.payment_type = newValue.name;
      }
      this.fetchCustomersData();
    },
    customer(newValue) {
      console.log("newValue ", newValue);
      this.payment_button_show = true;
      if (this.isInvoice == false) {
        this.formData.amount = 0;
        this.invoiceList = [];
        this.invoice = null;
        if (newValue != null) {
          this.customerCurrency = newValue.account.currency;
          this.formData.customer_id = newValue.id;
          this.formData.customer_company_id = newValue.company_id;
          if (this.formData.payment_type == "Prepayment") {
            this.fetchCustomerFinanceInvoices(newValue.id);
          } else if (this.formData.payment_type == "Settlement") {
            this.fetchCustomerSettlementInvoices(newValue.id);
          }
        }
      }
    },
    invoice(newValue) {
      if (newValue) {
        this.setPaymentAmountByInvoiceData(newValue.id);
      }
      if (this.formData.payment_type == "Settlement") {
        this.amountReady = true;
      }
    }
  },
  async mounted() {
    this.$nextTick(() => {
      this.loadData();
    });
  },
  methods: {
    ...mapActions("LoanRequest", ["loanRequestStatus"]),
    ...mapActions("payment", [
      "fetchCreatePayment",
      "addPayment",
      "updatePayment",
      "fetchEditPaymentData",
      "funderPOAddPayment"
    ]),
    ...mapActions("companyImpactOthers", [
      "addCompanyImpactOthers",
      "fetchCompanyImpactAccount",
      "getImpact"
    ]),
    ...mapActions("customer", [
      "viewCustomer",
      "fetchCustomers",
      "selectAllCustomers",
      "selectCustomer",
      "deleteCustomer",
      "deleteMultipleCustomers",
      "setSelectAllState"
    ]),
    ...mapActions("invoice", [
      "fetchInvoice",
      "SingleInvoiceArray",
      "financedUserInvoice",
      "needSettlementUserInvoice"
    ]),
    ...mapActions("po", ["fetchViewPO"]),
    ...mapActions("modal", ["openModal"]),
    async addPaymentMode() {
      this.openModal({
        title: "Add Payment Mode",
        componentName: "PaymentMode"
      });
    },
    invoiceWithAmount({ purchase_order_number, total }) {
      console.log(
        "purchase_order_number, total ",
        purchase_order_number,
        total
      );
      if (this.customerCurrency != null) {
        return `${purchase_order_number} (${this.$utils.formatGraphMoney(
          total,
          this.customerCurrency
        )})`;
      } else {
        return `${purchase_order_number} (${this.$utils.formatGraphMoney(
          total,
          this.defaultCurrencyForInput
        )})`;
      }
    },
    async loadData() {
      let response = await this.fetchCreatePayment();
      this.paymentNumAttribute = response.data.nextPaymentNumberAttribute;
      this.secondPaymentNumAttribute =
        response.data.nextSecondPaymentNumberAttribute;

      this.paymentPrefix = response.data.payment_prefix;
      this.formData.payment_date = moment(new Date()).toString();
      return true;
    },
    async loadPaymentData() {
      if (this.$route.name === "payments.po-prepayment.create") {
        this.payment_type = { name: "Prepayment" };
        this.formData.payment_type = "Prepayment";
      } else if (this.$route.name === "payments.po-settlement.create") {
        this.payment_type = { name: "Settlement" };
        this.formData.payment_type = "Settlement";
      }
      let invoiceAmountData = await this.fetchViewPO(this.$route.params.id);
      this.customer = invoiceAmountData.data.purchaseOrder.user;
      this.formData.customer_id = invoiceAmountData.data.purchaseOrder.user.id;
      this.formData.customer_company_id =
        invoiceAmountData.data.purchaseOrder.supplier.company_id;
      this.customerCurrency =
        invoiceAmountData.data.purchaseOrder.user.account.currency;
      this.invoice = invoiceAmountData.data.purchaseOrder;
    },
    async fetchCustomersData() {
      let customerRes = await this.fetchCustomers();
      this.customerList = customerRes.data.customers.data;
    },
    async fetchCustomerFinanceInvoices(customerID) {
      let data = { user_id: customerID };
      try {
        let response = await this.financedUserInvoice(data);
        if (response.data.result == "success") {
          this.invoiceList = response.data.invoices;
        } else if (response.data.result == "error") {
          window.toastr["error"](response.data.message);
        }
      } catch (error) {
        this.isLoading = false;
        window.toastr["error"](error.response.data.message);
      }
    },
    async fetchCustomerSettlementInvoices(customerID) {
      let data = { user_id: customerID };
      try {
        let response = await this.needSettlementUserInvoice(data);
        if (response.data.result == "success") {
          this.invoiceList = response.data.invoices;
        } else if (response.data.result == "error") {
          window.toastr["error"](response.data.message);
        }
      } catch (error) {
        this.isLoading = false;
        window.toastr["error"](error.response.data.message);
      }
    },

    async setPaymentAmountByInvoiceData(id) {
      let invoiceAmountData = await this.fetchViewPO(id);
      this.formData.purchase_order_id = invoiceAmountData.data.purchaseOrder.id;
      console.log(
        "invoiceAmountData.data.purchaseOrder ",
        invoiceAmountData.data.purchaseOrder,
        invoiceAmountData.data.purchaseOrder
          .purchase_order_pre_payment_requests[0]
      );
      if (this.formData.payment_type == "Prepayment") {
        this.formData.amount =
          invoiceAmountData.data.purchaseOrder.purchase_order_pre_payment_requests[0].amount;
        this.maxPayableAmount =
          invoiceAmountData.data.purchaseOrder.purchase_order_pre_payment_requests[0].amount;
        let data = {
          company_id: localStorage.getItem("selectedCompany"),
          guest_company_id: this.formData.customer_company_id
        };

        let checkCreditResponse = await this.getImpact(data);
        if (checkCreditResponse.data) {
          if (checkCreditResponse.data.credit_limit) {
            this.payment_button_show = false;
            let credit_limit = checkCreditResponse.data.credit_limit;
            let usedCredit = checkCreditResponse.data.used_credit / 100;
            let remainingCredit = credit_limit - usedCredit;
            if (credit_limit > usedCredit) {
              if (remainingCredit * 100 > this.formData.amount) {
                this.payment_button_show = true;
              } else {
                console.log("remainingCredit<formAmount");
                this.payment_button_show = false;
              }
            } else {
              console.log("credit_limit<used_credit");
              this.payment_button_show = false;
            }
          } else {
            console.log("set credit limit");
            this.payment_button_show = false;
          }
        }
      } else if (this.formData.payment_type == "Settlement") {
        this.formData.amount = invoiceAmountData.data.purchaseOrder.total;
        this.maxPayableAmount = invoiceAmountData.data.purchaseOrder.total;
        this.margin_rate =
          invoiceAmountData.data.purchaseOrder.purchase_order_pre_payment_requests[0].margin_rate;
        this.transaction_fees =
          invoiceAmountData.data.purchaseOrder.purchase_order_pre_payment_requests[0].transaction_fees;
        this.cashflowValue = invoiceAmountData.data.purchaseOrder.total;

        this.loanOfferedDate = invoiceAmountData.data.purchaseOrder.pre_payment_request_status.find(
          function(request, index) {
            if (request.status == "Offered") return true;
          }
        );

        // test it
        var date1 = new Date(Date());
        var date2 = new Date(
          invoiceAmountData.data.invoice.purchase_order_pre_payment_requests[0].promised_payment_date
        );
        this.tenor = this.dateDiffInDays(date1, date2);
      }
    },
    round(value, decimals) {
      return Number(Math.round(value + "e" + decimals) + "e-" + decimals);
    },
    // a and b are javascript Date objects
    dateDiffInDays(a, b) {
      const _MS_PER_DAY = 1000 * 60 * 60 * 24;
      // Discard the time and time-zone information.
      let utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
      let utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
      return Math.floor((utc2 - utc1) / _MS_PER_DAY);
    },
    async submitPaymentData() {
      this.$v.invoice.$touch();
      this.$v.payment_type.$touch();
      this.$v.customer.$touch();
      this.$v.formData.$touch();

      if (this.formData.payment_type == "Settlement") {
        this.$v.superadmin_settlement_notes.$touch();
      }
      if (this.$v.$invalid) {
        return true;
      }
      this.formData.payment_number =
        this.paymentPrefix + "-" + this.paymentNumAttribute;
      this.formData.second_payment_number =
        this.paymentPrefix + "-" + this.secondPaymentNumAttribute;

      let data = {
        ...this.formData,
        payment_method_id: this.formData.payment_method
          ? this.formData.payment_method.id
          : null,
        second_payment_method_id: this.formData.second_payment_method
          ? this.formData.second_payment_method.id
          : null,

        payment_date: moment(this.formData.payment_date).format("DD/MM/YYYY")
      };

      try {
        this.isLoading = true;
        let response = await this.funderPOAddPayment(data);
        if (response.data.success) {
          window.toastr["success"](this.$t("payments.created_message"));
          if (response.data.message) {
            window.toastr["success"](response.data.message);
          }
          this.$router.push("/funder/payments");

          return true;
        }
        if (response.data.error === "invalid_amount") {
          window.toastr["error"](this.$t("invalid_amount_message"));
          return false;
        }
        window.toastr["error"](response.data.error);
      } catch (err) {
        this.isLoading = false;
        if (err.response.data.errors.payment_number) {
          window.toastr["error"](err.response.data.errors.payment_number);
          return true;
        }
        window.toastr["error"](err.response.data.message);
      }
    }
  }
};
</script>
